import React from 'react';

import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import Table from '../../../../Table';

import ParticipationService from '../../../../../services/ParticipationService';
import LenderService from '../../../../../services/LenderService';
import convertFloatToPercentageString from '../../../../../helpers/convertFloatToPercentageString';
import LoanService from '../../../../../services/LoanService';

const createLink = (row) => {
  if (!row) {
    return null;
  }

  return row.style
    ? null
    : `/loans/${row.loan_id}/participations/${row.id}?tab=addOrEditParticipation`;
};

const createLoanInfoRow = (loan) => {
  if (!loan) {
    return null;
  }

  return {
    id: loan.id,
    label: `Loan: ${loan.rate_type} - ${convertFloatToPercentageString(
      loan.rate
    )}`,
    lender_id: loan.lender_id,
    rate: loan.participation_adjusted_rate,
    style: 'bg-slate-100 dark:bg-slate-900',
  };
};

export default function Participation({ loanId, loan, readOnly }) {
  const navigate = useNavigate();

  const { data: participations } = useQuery({
    queryKey: ['participations', loanId],
    queryFn: readOnly
      ? () => LoanService.getLoanParticipation(loanId)
      : () => ParticipationService.getParticipations({ loanId }),
    enabled: !!loanId,
  });

  const cols = [
    {
      title: 'Label',
      keys: ['label'],
      link: createLink,
    },
    {
      title: 'Participant',
      keys: readOnly ? ['_lender.name'] : ['lender_id'],
      type: readOnly ? 'text' : 'fetchableValue',
      fetchOptions: {
        queryKey: ['lender'],
        queryFn: async (value) => {
          if (!value) {
            return '';
          }

          const lender = await LenderService.getLender(value);
          return lender?.name;
        },
      },
      link: createLink,
    },
    {
      title: 'Yield',
      keys: ['rate'],
      format: convertFloatToPercentageString,
      link: createLink,
    },
    {
      title: 'Guaranteed',
      keys: ['guaranteed'],
      type: 'boolean',
      link: createLink,
      isHidden: (row) => row.style,
    },
    {
      title: 'Make whole',
      keys: ['make_whole'],
      type: 'boolean',
      link: createLink,
      isHidden: (row) => row.style,
    },
    {
      title: 'Start Date',
      keys: ['start_date'],
      type: 'date',
      isHidden: (row) => row.style,
    },
  ];

  const sortedParticipations = participations?.sort((a, b) => {
    // First, sort by `guaranteed` (true values come first)
    if (a.guaranteed === b.guaranteed) {
      // If `guaranteed` is equal, sort by `priority` (lower values come first)
      return a.priority - b.priority;
    }
    // `true` is considered greater than `false`, so we convert booleans to numbers
    // and subtract to get the correct order (true > false)
    return (b.guaranteed ? 1 : 0) - (a.guaranteed ? 1 : 0);
  });

  const participationsWithLoanInfo = [
    ...(sortedParticipations ?? []),
    createLoanInfoRow(loan),
  ];

  return (
    <div className="p-7 col-span-full sm:col-span-6 xl:col-span-4 bg-white dark:bg-slate-800 shadow-lg rounded-sm border border-slate-200 dark:border-slate-700 mb-10">
      <div className="flex justify-between">
        <h2 className="text-2xl text-slate-800 dark:text-slate-100 font-bold mb-6">
          Participation
        </h2>
        {!readOnly && (
          <button
            type="button"
            className="btn h-9 bg-indigo-500 md:max-w-sm text-white font-bold py-2 px-4 rounded        hover:bg-indigo-600"
            onClick={() =>
              navigate(
                `/loans/${loanId}/participations/new?tab=addOrEditParticipation`
              )
            }
          >
            <svg
              className="w-4 h-4 fill-current opacity-50 shrink-0"
              viewBox="0 0 16 16"
            >
              <path d="M15 7H9V1c0-.6-.4-1-1-1S7 .4 7 1v6H1c-.6 0-1 .4-1 1s.4 1 1 1h6v6c0 .6.4 1 1 1s1-.4 1-1V9h6c.6 0 1-.4 1-1s-.4-1-1-1z" />
            </svg>
            <span className="ml-2">Create Participation</span>
          </button>
        )}
      </div>
      <Table
        title="Participations"
        cols={cols}
        rows={participationsWithLoanInfo}
      />
    </div>
  );
}
